import BigNumber from 'bignumber.js'
import { computed } from 'vue'
import { useWalletStore, useAccountAPIStore, useNFTAPIStore } from '@/stores'
import type { Account } from '@/types'
import type { IWallet, WalletStatus } from '@/types/api/wallet'

class Wallet {
  id: string
  userId: string
  label: string
  defaultWallet: boolean
  status: WalletStatus
  createdAt: string
  updatedAt: string
  exportedAt: string | null
  order: number
  accounts!: Account[]

  // state는 status의 별칭 (V1 호환)
  get state(): WalletStatus {
    return this.status
  }
  set state(value: WalletStatus) {
    this.status = value
  }

  constructor({ wallet }: { wallet: IWallet }) {
    this.id = wallet.id
    this.userId = wallet.userId
    this.label = wallet.label
    this.defaultWallet = wallet.defaultWallet
    this.status = wallet.status
    this.createdAt = wallet.createdAt
    this.updatedAt = wallet.updatedAt
    this.exportedAt = wallet.exportedAt
    this.order = wallet.order

    const walletStore = useWalletStore()
    const computedAccounts = computed(() => {
      const filtered = walletStore.accountList.filter((account) => {
        return account.walletId === this.id
      })
      return filtered
    })
    Object.defineProperty(this, 'accounts', {
      get(): Account[] {
        return computedAccounts.value
      },
    })
  }

  get totalBalance(): string {
    return this.accounts
      .reduce((balance, account) => {
        const accountBalance = account.amount.ethValue.plus(balance)
        const tokenBalances = account.tokens?.reduce((tokenBalance, token) => {
          return token.amount.ethValue.plus(tokenBalance)
        }, new BigNumber(0))
        return accountBalance.plus(tokenBalances ?? 0)
      }, new BigNumber(0))
      .toFixed()
  }

  get totalExchangedBalance(): string {
    return this.accounts
      .reduce((balance, account) => {
        const accountValue = account.amount
          .calculatePrice(account.coin?.priceInfo)
          .plus(balance)
        const tokenValues = account.tokens?.reduce((tokenValue, token) => {
          return new BigNumber(
            token.amount.calculatePrice(token.coin?.priceInfo),
          ).plus(tokenValue)
        }, new BigNumber(0))
        return accountValue.plus(tokenValues ?? 0)
      }, new BigNumber(0))
      .toFixed()
  }

  async fetchAccounts(force: boolean | undefined = false) {
    const accountAPIStore = useAccountAPIStore()
    await accountAPIStore.fetchAccountList(
      {
        walletId: this.id,
      },
      force,
    )
  }

  async refreshNFT() {
    const nftAPIStore = useNFTAPIStore()
    const walletStore = useWalletStore()
    const accountIdList = walletStore.accountList
      .filter((account) => {
        return account.walletId === walletStore.selectedWalletId
      })
      .map((account) => account.id)
    await nftAPIStore.refreshNFTList({
      accountIds: accountIdList,
      walletId: walletStore.selectedWalletId,
    })
  }
}

class WalletFactory {
  private static instance: WalletFactory
  private constructor() {}

  public static getInstance(): WalletFactory {
    if (!WalletFactory.instance) {
      WalletFactory.instance = new WalletFactory()
    }

    return WalletFactory.instance
  }

  makeWallet(wallet: IWallet) {
    const result = new Wallet({
      wallet,
    })
    return result
  }
}

export { Wallet, WalletFactory }
